/**
 * Composable for cancel-with-feedback UX on tasks and builds.
 *
 * Status-change detection is handled by module-level event bus subscriptions
 * (set up once on first `useCancelWithFeedback()` call). This makes feedback
 * independent of whichever component is currently mounted.
 *
 * Feedback is queued via the shared `feedbackQueue` module. A single
 * `<v-snackbar-queue>` in App.vue (via `useFeedbackQueue()`) displays
 * messages one at a time in sequence, so no messages are lost.
 */
import { InjectionKey, reactive } from "vue";
import { sdk } from "@/js/sdk";
import { shortenID } from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { isTaskTerminated, TaskStatus, TaskStatusE } from "@/types/api/tasks";
import { isBuildTerminated, BuildStatus, BuildStatusE } from "@/types/api/builds";
import { useBuildsStore } from "@/stores/builds";
import { useTasksStore } from "@/stores/tasks";
import { pushFeedback } from "@/composables/feedbackQueue";

export interface CancelOptions {
	onSent?: () => void;
}

interface CancelChannelConfig<S extends string> {
	label: string;
	apiCall: (id: string) => Promise<unknown>;
	cancelledStatus: S;
	isTerminated: (status: S) => boolean;
}

export interface CancelStatusResult {
	wasCancelledByUser: boolean;
}

interface CancelChannel<S extends string> {
	isCancelling: (id: string | undefined) => boolean;
	cancel: (id: string | undefined, options?: CancelOptions) => Promise<void>;
	handleStatusUpdate: (id: string | undefined, status: S | undefined) => CancelStatusResult;
}

export const CancelFeedbackKey: InjectionKey<CancelWithFeedback> = Symbol("cancelFeedback");

export const CANCEL_FEEDBACK_NOOP: CancelWithFeedback = {
	isCancellingTask: () => false,
	isCancellingBuild: () => false,
	cancelTask: async () => {},
	cancelBuild: async () => {},
	handleTaskStatusUpdate: () => ({ wasCancelledByUser: false }),
	handleBuildStatusUpdate: () => ({ wasCancelledByUser: false }),
};

export interface CancelWithFeedback {
	isCancellingTask: (id: string | undefined) => boolean;
	isCancellingBuild: (id: string | undefined) => boolean;
	cancelTask: (id: string | undefined, options?: CancelOptions) => Promise<void>;
	cancelBuild: (id: string | undefined, options?: CancelOptions) => Promise<void>;
	handleTaskStatusUpdate: (id: string | undefined, status: TaskStatus | undefined) => CancelStatusResult;
	handleBuildStatusUpdate: (id: string | undefined, status: BuildStatus | undefined) => CancelStatusResult;
}

interface ChannelState {
	pendingCancels: Set<string>;
}

/** Module-level state per channel — survives component unmounts. */
const channelStates = new Map<string, ChannelState>();

function getChannelState(label: string): ChannelState {
	let state = channelStates.get(label);
	if (!state) {
		state = { pendingCancels: reactive(new Set()) };
		channelStates.set(label, state);
	}
	return state;
}

function createCancelChannel<S extends string>(config: CancelChannelConfig<S>): CancelChannel<S> {
	const { pendingCancels } = getChannelState(config.label);

	function removePendingCancel(id: string) {
		pendingCancels.delete(id);
	}

	function isCancelling(id: string | undefined): boolean {
		return !!id && pendingCancels.has(id);
	}

	async function cancel(id: string | undefined, options?: CancelOptions) {
		if (!id || pendingCancels.has(id)) return;
		pendingCancels.add(id);
		try {
			await config.apiCall(id);
			options?.onSent?.();
		} catch (error) {
			monitoringCaptureError(error, `Cancel active ${config.label}`);
			// If handleStatusUpdate already processed the terminal state, skip user feedback.
			if (!pendingCancels.has(id)) return;
			removePendingCancel(id);
			pushFeedback(`Failed to cancel ${config.label} ${shortenID(id)}. Please try again.`, "error");
		}
	}

	function handleStatusUpdate(id: string | undefined, status: S | undefined): CancelStatusResult {
		if (!id || !status) return { wasCancelledByUser: false };

		if (!config.isTerminated(status)) {
			return { wasCancelledByUser: false };
		}

		if (!pendingCancels.has(id)) return { wasCancelledByUser: false };

		if (status === config.cancelledStatus) {
			removePendingCancel(id);
			pushFeedback(`${capitalize(config.label)} ${shortenID(id)} cancelled successfully.`, "success");
			return { wasCancelledByUser: true };
		}

		removePendingCancel(id);
		pushFeedback(`${capitalize(config.label)} ${shortenID(id)} finished before cancellation took effect.`, "info");
		return { wasCancelledByUser: false };
	}

	return { isCancelling, cancel, handleStatusUpdate };
}

function capitalize(s: string): string {
	return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Subscribe to the builds/tasks store event buses at module level so that
 * cancel feedback works regardless of which component is currently mounted.
 * Called once on the first `useCancelWithFeedback()` invocation.
 */
let eventBusSubscribed = false;
function subscribeToStoreEventBuses(buildChannel: CancelChannel<BuildStatus>, taskChannel: CancelChannel<TaskStatus>) {
	if (eventBusSubscribed) return;
	eventBusSubscribed = true;

	const buildsStore = useBuildsStore();
	buildsStore.buildEventBus.on((event) => {
		buildChannel.handleStatusUpdate(event.buildData.BuildID, event.buildData.Status);
	});

	const tasksStore = useTasksStore();
	tasksStore.taskEventBus.on((event) => {
		taskChannel.handleStatusUpdate(event.taskData.TaskID, event.taskData.Status);
	});
}

export function useCancelWithFeedback(): CancelWithFeedback {
	const taskChannel = createCancelChannel<TaskStatus>({
		label: "task",
		apiCall: (id) => sdk.tasks.cancel(id),
		cancelledStatus: TaskStatusE.Cancelled,
		isTerminated: isTaskTerminated,
	});

	const buildChannel = createCancelChannel<BuildStatus>({
		label: "build",
		apiCall: (id) => sdk.builds.cancel(id),
		cancelledStatus: BuildStatusE.Cancelled,
		isTerminated: isBuildTerminated,
	});

	subscribeToStoreEventBuses(buildChannel, taskChannel);

	return {
		isCancellingTask: taskChannel.isCancelling,
		isCancellingBuild: buildChannel.isCancelling,
		cancelTask: taskChannel.cancel,
		cancelBuild: buildChannel.cancel,
		handleTaskStatusUpdate: taskChannel.handleStatusUpdate,
		handleBuildStatusUpdate: buildChannel.handleStatusUpdate,
	};
}
